import * as THREE from 'three';
import { BLACK, BONE, DWOOD, GOLD, basic, cached, emis, metal, smoke, std, stone } from '../materials.ts';
import { G, M, Ribbon, V, eyesGlow, flame, glowSprite, limb, sword, wisp } from '../prims.ts';
import { figure } from './heroes.ts';
import { LIGHT_K, begin, bonesOf, done, joint, pointLight, type BaseOpts, type Model } from './types.ts';

/*
 * The bosses are rigged (see `joint()` and anim/creatures/bosses.ts): their builders place every part with the
 * same coordinates as before, under the bone that carries it, and the clips do all the moving. `tick()` only
 * boils (ribbons, flames, light, drifting shades and motes).
 */

const UP = new THREE.Vector3(0, 1, 0);

/** A bone turned `yaw` about Y at rest (its local axes turn with it); its `abs` frame keeps the parent's coordinates. */
const yjoint = (parentAbs: THREE.Object3D, at: THREE.Vector3, yaw: number, name: string): { p: THREE.Group; abs: THREE.Group } => {
  const p = new THREE.Group();
  p.name = name;
  p.position.copy(at);
  p.rotation.y = yaw;
  parentAbs.add(p);
  const abs = new THREE.Group();
  abs.rotation.y = -yaw;
  abs.position.copy(at).negate().applyAxisAngle(UP, -yaw);
  p.add(abs);
  return { p, abs };
};

/** A glowing tube along jittered points, optionally cut at `cut` into two bones (`lower` takes the rest). */
const tubeAlong = (parent: THREE.Object3D, jit: THREE.Vector3[], r: number, mat: THREE.Material, lower?: THREE.Object3D, cut = 0): void => {
  const run = (into: THREE.Object3D, pts: THREE.Vector3[]): void => {
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.05), pts.length * 2, r, 3), mat, into);
  };
  if (lower && cut > 0 && cut < jit.length - 1) {
    run(parent, jit.slice(0, cut + 1));
    run(lower, jit.slice(cut));
  } else run(parent, jit);
};

/** La Bruja de Ceniza: mid-campaign boss with a green-fire lantern staff. */
export const witch = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 41);
  const { g, rng } = c;
  // Bones: the robe pivots on the ground, the hunched chest on it; the staff rides the left hand, the lantern hangs off its hook.
  const robeB = joint(g, V(0, 0.02, 0), 'robe');
  const chestB = joint(robeB.abs, V(0, 1.3, 0.12), 'chest');
  const headB = joint(chestB.abs, V(0, 1.5, 0.17), 'head');
  const hairB = joint(headB.abs, V(0, 1.74, 0.15), 'hair');
  const armB = [-1, 1].map((k) => joint(chestB.abs, V(k * 0.2, 1.4, 0.15), k < 0 ? 'armL' : 'armR'));
  const foreB = [-1, 1].map((k, i) => joint(armB[i]!.abs, V(k * 0.38, 1.1, 0.3), k < 0 ? 'foreL' : 'foreR'));
  const staffB = joint(foreB[0]!.abs, V(-0.4, 0.85, 0.42), 'staff');
  const lanB = joint(staffB.abs, V(-0.24, 1.95, 0.4), 'lantern');
  const rag = std('#2b2725', { side: THREE.DoubleSide, roughness: 1 }), skin = std('#7b7f6a', { roughness: 0.9 });
  const geo = new THREE.ConeGeometry(0.42, 1.5, 14, 5, true);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) if (pos.getY(i) < -0.7) pos.setY(i, pos.getY(i) + rng() * 0.2);
  geo.computeVertexNormals();
  const robe = M(geo, rag, robeB.abs);
  robe.position.y = 0.75;
  robe.rotation.x = 0.12;
  const head = M(new THREE.SphereGeometry(0.15, 10, 8), skin, headB.abs);
  head.position.set(0, 1.62, 0.2);
  head.scale.set(0.9, 1.1, 1.1);
  const nose = M(new THREE.ConeGeometry(0.03, 0.14, 5), skin, headB.abs);
  nose.position.set(0, 1.6, 0.36);
  nose.rotation.x = Math.PI / 2 + 0.3;
  eyesGlow(headB.abs, '#9dff6a', 1.66, 0.33, 0.05);
  const hairM = std('#8f8a82', { roughness: 1 });
  const hair: Ribbon[] = [];
  for (let i = 0; i < 7; i++) {
    const a = -1.4 + i * 0.47;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 6; j++) pts.push(V(Math.sin(a) * 0.15 * (1 + j * 0.15), 1.72 - j * 0.14, Math.cos(a) * 0.12 + 0.15 - j * 0.03));
    hair.push(new Ribbon(hairB.abs, pts, 0.015, hairM, rng, 0.02));
  }
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    limb(armB[i]!.abs, V(k * 0.2, 1.4, 0.15), V(k * 0.38, 1.1, 0.3), 0.035, 0.03, rag, 6);
    limb(foreB[i]!.abs, V(k * 0.38, 1.1, 0.3), V(k * 0.4, 0.85, 0.42), 0.025, 0.022, skin, 6);
  }
  const sp = [V(-0.42, 0, 0.4), V(-0.4, 0.8, 0.42), V(-0.44, 1.4, 0.4), V(-0.36, 1.9, 0.42), V(-0.24, 1.98, 0.4)];
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(sp), 16, 0.022, 5), DWOOD, staffB.abs);
  const lan = G(lanB.abs, -0.24, 1.84, 0.4);
  const wire = cached('wire#2a2a2a', () => new THREE.MeshBasicMaterial({ color: '#2a2a2a', wireframe: true }));
  M(new THREE.CylinderGeometry(0.06, 0.07, 0.14, 6, 1, true), wire, lan);
  const fl = flame(lan, V(0, -0.06, 0), 0.6, rng, '#58ff6a');
  const L = pointLight(c, 0x7dff6a, 0.8, 3, V(-0.24, 1.8, 0.4));
  // The lantern's swing lives in the clips now; the frames only boil.
  const t = (): void => {
    for (const h of hair) h.step();
    fl.flick();
    L.intensity = (0.6 + rng() * 0.4) * LIGHT_K;
  };
  return done(c, 2.0, t, undefined, bonesOf(c, {
    robe: robeB.p, chest: chestB.p, head: headB.p, hair: hairB.p, armL: armB[0]!.p, foreL: foreB[0]!.p, staff: staffB.p, lantern: lanB.p, armR: armB[1]!.p, foreR: foreB[1]!.p,
  }));
};

/** El Caparazón Hueco: hollow titan-turtle shell on bone legs that spits shades. */
export const shell = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 42);
  const { g, rng } = c;
  // Bones: the carapace, and each of the six legs at the hip and the knee, turned so its local +Z points out along the leg.
  const shB = joint(g, V(0, 0.6, 0), 'carapace');
  const sh = shB.abs;
  const st = stone('#2c2826');
  const crack = emis('#c9a2ff', '#8b4dff', 1.1);
  const dome = M(new THREE.SphereGeometry(0.62, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), st, sh);
  dome.position.y = 0.62;
  dome.scale.y = 0.75;
  const hollow = M(new THREE.CircleGeometry(0.55, 16), BLACK, sh);
  hollow.rotation.x = Math.PI / 2;
  hollow.position.y = 0.61;
  g.updateMatrixWorld(true);
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    const pl = M(new THREE.CylinderGeometry(0.18, 0.2, 0.06, 6), st, sh);
    const r = 0.42;
    pl.position.set(Math.cos(a) * r, 0.62 + Math.sqrt(Math.max(0, 0.62 * 0.62 - r * r)) * 0.75, Math.sin(a) * r);
    pl.lookAt(pl.position.x * 3, 3, pl.position.z * 3);
    pl.rotateX(Math.PI / 2);
  }
  const ribs: Ribbon[] = [];
  for (let i = 0; i < 6; i++) {
    const a = i * 1.05;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 6; j++) {
      const b = (j / 5) * 1.3;
      pts.push(V(Math.cos(a) * Math.sin(b) * 0.63, 0.62 + Math.cos(b) * 0.47, Math.sin(a) * Math.sin(b) * 0.63));
    }
    ribs.push(new Ribbon(sh, pts, 0.012, crack, rng, 0.02));
  }
  const legBones: Record<string, THREE.Object3D> = {};
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    const hip = V(Math.cos(a) * 0.5, 0.6, Math.sin(a) * 0.5), knee = V(Math.cos(a) * 0.85, 0.75, Math.sin(a) * 0.85), ft = V(Math.cos(a) * 0.95, 0.02, Math.sin(a) * 0.95);
    const thigh = yjoint(sh, hip, Math.PI / 2 - a, `leg${i}`);
    const shin = yjoint(thigh.abs, knee, Math.PI / 2 - a, `knee${i}`);
    limb(thigh.abs, hip, knee, 0.04, 0.035, BONE, 6);
    limb(shin.abs, knee, ft, 0.03, 0.012, BONE, 6);
    legBones[`leg${i}`] = thigh.p;
    legBones[`knee${i}`] = shin.p;
  }
  const shadeM = smoke('#1d1730', 0.7, { depthWrite: true });
  const shades: THREE.Group[] = [];
  for (let i = 0; i < 3; i++) {
    const s = G(sh);
    M(new THREE.SphereGeometry(0.07, 7, 5), shadeM, s);
    eyesGlow(s, '#c9b8ff', 0.02, 0.06, 0.025, 0.012);
    shades.push(s);
  }
  glowSprite(sh, '#8b4dff', 1.2, V(0, 0.75, 0), 0.35);
  // Its gait lives in the clips now; the frames only boil (cracks, and the shades drifting out of the hollow).
  const t = (n: number): void => {
    for (const r of ribs) r.step();
    shades.forEach((s, i) => {
      const p = (n * 0.05 + i / 3) % 1;
      s.position.set(Math.cos(i * 2.1) * 0.4 * p, 0.7 + p * 0.9 - p * p * 0.7, Math.sin(i * 2.1) * 0.4 * p);
    });
  };
  t(0);
  return done(c, 1.3, t, undefined, bonesOf(c, { carapace: shB.p, ...legBones }));
};

/** El Rey sin Rostro: armoured faceless king with a planted greatsword. Has a rig. */
export const faceless = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 43);
  const { g, R, rng } = c;
  const arm = metal('#22232a', { roughness: 0.3 }), trim = metal('#6b5a2a', { roughness: 0.35 });
  const f = figure({
    legs: arm, boots: arm, torso: metal('#22232a', { flatShading: true, roughness: 0.3 }), shoulders: trim, arms: arm, hands: arm, skin: BLACK, cape: '#3a0f16',
  });
  g.add(f.fig);
  f.fig.scale.setScalar(1.15);
  const h = f.headAbs;
  const hood = M(new THREE.SphereGeometry(0.14, 12, 8, 0, Math.PI * 2, 0, Math.PI / 1.6), std('#14090c', { roughness: 1 }), h);
  hood.position.set(0, 1.66, -0.02);
  hood.rotation.x = -0.25;
  eyesGlow(h, '#c98bff', 1.64, 0.12, 0.04);
  const crown = G(h, 0, 1.79, 0);
  M(new THREE.CylinderGeometry(0.12, 0.13, 0.05, 10, 1, true), metal('#c9a24a', { roughness: 0.3, side: THREE.DoubleSide }), crown);
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    M(new THREE.ConeGeometry(0.018, 0.12, 5), GOLD, crown).position.set(Math.cos(a) * 0.125, 0.08, Math.sin(a) * 0.125);
  }
  const gs = sword(1.05, metal('#3b3d47', { roughness: 0.25 }), trim);
  gs.position.set(0, 0.02, 0.32);
  f.rhAbs.add(gs);
  const rune = emis('#d6a8ff', '#9b4dff', 1);
  for (let i = 0; i < 4; i++) M(new THREE.BoxGeometry(0.02, 0.05, 0.014), rune, gs).position.set(0, 0.35 + i * 0.18, 0);
  const ribs: Ribbon[] = [];
  const wm = basic('#3a1530', 0.45);
  for (let i = 0; i < 3; i++) ribs.push(new Ribbon(f.fig, wisp(R() * 0.3, -0.3, 1.2, 7), 0.015, wm, rng, 0.05));
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  return done(c, 2.15, t, f.rig);
};

/**
 * El Custodio de la Médula: the crypt's warden, a hunched colossus of old bone that carries a slab of
 * titan shell on its back. The marrow in its chest and the cracks of the shell burn violet; its
 * fists are knuckles of fused stone.
 */
export const warden = (o?: BaseOpts & { molten?: boolean }): Model => {
  const c = begin(o, 0.9, 45);
  const { g, rng } = c;
  // Bones: pelvis and chest (the spine bends at the small of the back), the shell slab, skull and jaw, arms and legs.
  const hipsB = joint(g, V(0, 0.66, -0.04), 'hips');
  const chestB = joint(hipsB.abs, V(0, 0.92, -0.03), 'chest');
  const shellB = joint(chestB.abs, V(0, 1.18, -0.22), 'shell');
  const headB = joint(chestB.abs, V(0, 1.27, 0.22), 'head');
  const jawB = joint(headB.abs, V(0, 1.3, 0.3), 'jaw');
  const legB = [-1, 1].map((k) => joint(g, V(k * 0.22, 0.62, -0.05), k < 0 ? 'legL' : 'legR'));
  const shinB = [-1, 1].map((k, i) => joint(legB[i]!.abs, V(k * 0.3, 0.32, 0.08), k < 0 ? 'shinL' : 'shinR'));
  const armB = [-1, 1].map((k) => joint(chestB.abs, V(k * 0.3, 1.22, 0.12), k < 0 ? 'armL' : 'armR'));
  const foreB = [-1, 1].map((k, i) => joint(armB[i]!.abs, V(k * 0.42, 0.82, 0.2), k < 0 ? 'foreL' : 'foreR'));
  const hips = hipsB.abs, chest = chestB.abs;
  // Molten (the crypt in embers): the same bones charred black and split by seams of fire, marrow gone to magma.
  const hotW = o?.molten === true;
  const bone = hotW ? std('#3b3331', { roughness: 0.9, flatShading: true }) : std('#cfc3a4', { roughness: 0.85, flatShading: true });
  const old = hotW ? std('#231e1d', { roughness: 0.95, flatShading: true }) : std('#8f8670', { roughness: 0.9, flatShading: true });
  const shellM = stone(hotW ? '#1a1717' : '#2e3127');
  const plate = stone(hotW ? '#262120' : '#3d4233');
  const marrow = hotW ? emis('#fff0c8', '#ff8a2a', 2.4) : emis('#e7c9ff', '#9b4dff', 1.6);
  const crackM = hotW ? emis('#ff8a3d', '#ff3d0a', 1.8) : emis('#d6b3ff', '#8b4dff', 1.2);
  const fistM = stone(hotW ? '#1e1a19' : '#4a4740');
  const glowC = hotW ? '#ff7a1a' : '#9b4dff';
  /** A jagged seam of fire laid along a bone (molten only); with `lower`, the points from `cut` on ride that bone. */
  const seam = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.011, lower?: THREE.Object3D, cut = 0): void => {
    if (!hotW) return;
    const jit = pts.map((p, i) => (i === 0 ? p : p.clone().add(V((rng() - 0.5) * 0.03, (rng() - 0.5) * 0.03, (rng() - 0.5) * 0.02))));
    tubeAlong(parent, jit, r, crackM, lower, cut);
  };
  // Legs: short, thick, bent; feet are splayed bone.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    limb(legB[i]!.abs, V(k * 0.22, 0.62, -0.05), V(k * 0.3, 0.32, 0.08), 0.09, 0.075, bone, 7);
    limb(shinB[i]!.abs, V(k * 0.3, 0.32, 0.08), V(k * 0.28, 0.04, -0.02), 0.075, 0.06, old, 7);
    for (let t = -1; t <= 1; t++) {
      const toe = M(new THREE.ConeGeometry(0.03, 0.14, 5), bone, shinB[i]!.abs);
      toe.position.set(k * 0.28 + t * 0.05, 0.03, 0.08);
      toe.rotation.x = Math.PI / 2;
    }
    seam(legB[i]!.abs, [V(k * 0.24, 0.6, 0.03), V(k * 0.29, 0.44, 0.13), V(k * 0.31, 0.3, 0.15), V(k * 0.29, 0.12, 0.06)], 0.011, shinB[i]!.abs, 2);
  }
  // Pelvis and spine, hunched forward.
  M(new THREE.BoxGeometry(0.42, 0.14, 0.24), old, hips).position.set(0, 0.68, -0.04);
  const spine: THREE.Vector3[] = [V(0, 0.72, -0.08), V(0, 0.95, -0.02), V(0, 1.15, 0.08), V(0, 1.3, 0.2)];
  for (let i = 0; i < spine.length - 1; i++) limb(i === 0 ? hips : chest, spine[i] as THREE.Vector3, spine[i + 1] as THREE.Vector3, 0.07, 0.06, bone, 6);
  // Ribcage around the burning marrow.
  const core = M(new THREE.IcosahedronGeometry(0.13, 1), marrow, chest);
  core.position.set(0, 1.08, 0.12);
  const coreGlow = glowSprite(chest, glowC, hotW ? 1.3 : 1.1, V(0, 1.08, 0.18), 0.55, true);
  seam(hips, [V(0, 0.74, -0.02), V(0.02, 0.95, 0.04), V(-0.01, 1.15, 0.15), V(0, 1.29, 0.27)], 0.013, chest, 1);
  for (let i = 0; i < 5; i++) {
    const y = 0.92 + i * 0.075;
    for (const k of [-1, 1]) {
      const pts = [V(0, y, 0.0), V(k * 0.22, y + 0.02, 0.06), V(k * 0.24, y - 0.02, 0.2), V(k * 0.08, y - 0.04, 0.3)];
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 8, 0.018, 5), bone, chest);
    }
  }
  // The slab of titan shell on its back, plated and cracked with violet light.
  const back = G(shellB.abs, 0, 1.12, -0.18);
  back.rotation.x = -0.35;
  const dome = M(new THREE.SphereGeometry(0.5, 14, 7, 0, Math.PI * 2, 0, Math.PI / 2.2), shellM, back);
  dome.scale.set(1.05, 0.55, 0.9);
  dome.rotation.x = -Math.PI / 2;
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const pl = M(new THREE.CylinderGeometry(0.13, 0.15, 0.05, 6), plate, back);
    pl.position.set(Math.cos(a) * 0.3, Math.sin(a) * 0.26, -0.22);
    pl.rotation.x = Math.PI / 2;
  }
  M(new THREE.CylinderGeometry(0.15, 0.17, 0.05, 6), plate, back).position.set(0, 0, -0.28);
  const cracks: Ribbon[] = [];
  for (let i = 0; i < 4; i++) {
    const a = i * 1.57 + 0.4;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 5; j++) pts.push(V(Math.cos(a) * (0.05 + j * 0.1), Math.sin(a) * (0.05 + j * 0.09), -0.27 + j * 0.04));
    cracks.push(new Ribbon(back, pts, 0.01, crackM, rng, 0.015));
  }
  // Spikes of bone along the shell's rim.
  for (let i = 0; i < 9; i++) {
    const a = -0.3 + (i / 8) * (Math.PI + 0.6);
    const sp = M(new THREE.ConeGeometry(0.035, 0.22, 5), bone, back);
    sp.position.set(Math.cos(a) * 0.5, Math.sin(a) * 0.3 + 0.05, -0.05);
    sp.rotation.z = a - Math.PI / 2;
  }
  // Skull: long, horned, sunk low between the shoulders.
  const head = G(headB.abs, 0, 1.36, 0.32);
  const skull = M(new THREE.SphereGeometry(0.15, 10, 8), bone, head);
  skull.scale.set(1, 0.9, 1.25);
  const jaw = M(new THREE.BoxGeometry(0.17, 0.05, 0.2), old, G(jawB.abs, 0, 1.36, 0.32));
  jaw.position.set(0, -0.1, 0.06);
  for (const k of [-1, 1]) {
    const pts = [V(k * 0.1, 0.06, -0.02), V(k * 0.24, 0.14, -0.08), V(k * 0.3, 0.3, -0.04), V(k * 0.26, 0.42, 0.06)];
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 10, 0.022, 5), old, head);
  }
  eyesGlow(head, hotW ? '#ffb347' : '#c98bff', 0.02, 0.15, 0.06, 0.026);
  seam(head, [V(-0.02, 0.13, 0.05), V(0.03, 0.08, 0.15), V(-0.01, 0.02, 0.2)]);
  seam(head, [V(0.1, 0.06, -0.05), V(0.13, 0.0, 0.06), V(0.1, -0.05, 0.14)]);
  // Arms: long bone, fists of fused stone (the upper arm and the forearm each on its bone, in the shoulder's old frame).
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const sh = G(armB[i]!.abs, k * 0.3, 1.22, 0.12);
    const fo = G(foreB[i]!.abs, k * 0.3, 1.22, 0.12);
    M(new THREE.SphereGeometry(0.09, 8, 6), bone, sh);
    limb(sh, V(0, 0, 0), V(k * 0.12, -0.4, 0.08), 0.06, 0.05, bone, 6);
    limb(fo, V(k * 0.12, -0.4, 0.08), V(k * 0.14, -0.78, 0.18), 0.05, 0.045, old, 6);
    const fist = M(new THREE.DodecahedronGeometry(0.16, 0), fistM, fo);
    fist.position.set(k * 0.15, -0.9, 0.2);
    fist.scale.set(1, 0.85, 1.1);
    for (let j = 0; j < 3; j++) {
      const kn = M(new THREE.ConeGeometry(0.03, 0.09, 4), bone, fist);
      kn.position.set((j - 1) * 0.07, 0.04, 0.13);
      kn.rotation.x = Math.PI / 2;
    }
    seam(sh, [V(0, -0.04, 0.06), V(k * 0.07, -0.22, 0.12), V(k * 0.12, -0.42, 0.13), V(k * 0.13, -0.62, 0.22), V(k * 0.15, -0.78, 0.25)], 0.011, fo, 2);
    // Fists of stone cracked open on fire.
    if (hotW) M(new THREE.IcosahedronGeometry(0.1, 0), marrow, fo).position.set(k * 0.15, -0.9, 0.27);
  }
  // Molten: fire licking out of the shell and off the shoulders.
  const fires = hotW ? [flame(back, V(0, 0.22, -0.25), 0.9, rng, '#ff5a1f'), flame(chest, V(-0.3, 1.32, 0.08), 0.55, rng, '#ff5a1f'), flame(chest, V(0.3, 1.32, 0.08), 0.55, rng, '#ff5a1f')] : [];
  if (hotW) glowSprite(hips, '#ff4f1a', 1.6, V(0, 0.8, 0.15), 0.2);
  const wisps: Ribbon[] = [];
  const wm = hotW ? basic('#2c2420', 0.45) : basic('#5a2a7a', 0.4);
  for (let i = 0; i < 3; i++) wisps.push(new Ribbon(g, wisp((rng() - 0.5) * 0.4, -0.2, 1.4, 7), hotW ? 0.03 : 0.02, wm, rng, 0.05));
  const L = pointLight(c, hotW ? 0xff6a2a : 0x9b4dff, 1.2, 4, V(0, 1.1, 0.3));
  // The swings live in the clips now; the frames only boil and pulse.
  const t = (n: number): void => {
    for (const r of cracks) r.step();
    for (const r of wisps) r.step();
    for (const f of fires) f.flick();
    const pulse = 0.4 + ((n * 3) % 10) / 20;
    coreGlow.material.opacity = pulse;
    L.intensity = (0.8 + pulse) * LIGHT_K;
  };
  t(0);
  return done(c, 1.75, t, undefined, bonesOf(c, {
    hips: hipsB.p, chest: chestB.p, shell: shellB.p, head: headB.p, jaw: jawB.p,
    armL: armB[0]!.p, foreL: foreB[0]!.p, armR: armB[1]!.p, foreR: foreB[1]!.p, legL: legB[0]!.p, shinL: shinB[0]!.p, legR: legB[1]!.p, shinR: shinB[1]!.p,
  }));
};

/**
 * El Señor del Magma: the boss of the Ascuas incursions. A hulking lord of cooled black basalt, split
 * by seams of living fire; a molten heart burns behind the slabs of its chest, a crown of obsidian
 * horns rises from its sunken head and it drags a great stone maul banded in molten iron.
 */
export const magmaLord = (o?: BaseOpts): Model => {
  const c = begin(o, 0.95, 46);
  const { g, rng } = c;
  const basalt = stone('#1a1717');
  const crust = stone('#24201f');
  const ash = stone('#332d2b');
  const obsidian = std('#0d0a0b', { roughness: 0.22, metalness: 0.25, flatShading: true, bumpScale: 0 });
  const seam = emis('#ff8a3d', '#ff3d0a', 1.8);
  const heart = emis('#fff0c8', '#ff8a2a', 2.4);
  const hot = emis('#ffd59a', '#ff6a1f', 2.0);
  // A seam of fire laid along a surface: a thin glowing tube that wanders a little (with `lower`, cut at `cut`).
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.01, lower?: THREE.Object3D, cut = 0): void => {
    // Doubled points, jittered and joined by short straight runs: a jagged split, not a smooth cord.
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(i === 0 ? p : p.clone().add(V((rng() - 0.5) * 0.04, (rng() - 0.5) * 0.04, (rng() - 0.5) * 0.02)));
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V((rng() - 0.5) * 0.05, (rng() - 0.5) * 0.03, (rng() - 0.5) * 0.02)));
    });
    tubeAlong(parent, jit, r, seam, lower, cut);
  };
  /** A slab of rock, a little crooked. */
  const slab = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, s: number, sx = 1, sy = 1, sz = 1): THREE.Mesh => {
    const m = M(new THREE.DodecahedronGeometry(s, 0), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    m.rotation.set(rng() * 3, rng() * 3, rng() * 3);
    return m;
  };

  // Legs: two pillars of stacked boulders, knees glowing through the cracks; flat clawed feet.
  // Bones at the hips (their parts in the hip's own frame) and the knees.
  const bones: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const side = k < 0 ? 'L' : 'R';
    const hipB = joint(g, V(k * 0.26, 0.74, -0.02), `leg${side}`);
    const hip = hipB.p;
    const shinB = joint(hip, V(0, -0.34, 0.05), `shin${side}`);
    const shin = shinB.abs;
    bones[`leg${side}`] = hipB.p;
    bones[`shin${side}`] = shinB.p;
    slab(hip, basalt, 0, -0.14, 0, 0.17, 1, 1.25, 1);
    slab(shin, crust, k * 0.02, -0.42, 0.04, 0.15, 1, 1.1, 1);
    M(new THREE.IcosahedronGeometry(0.07, 0), hot, hip).position.set(0, -0.3, 0.1);
    slab(shin, basalt, 0, -0.64, 0.02, 0.15, 1.1, 0.8, 1.25);
    for (let t = -1; t <= 1; t++) {
      const toe = M(new THREE.ConeGeometry(0.04, 0.14, 4), obsidian, shin);
      toe.position.set(t * 0.07, -0.7, 0.18);
      toe.rotation.x = Math.PI / 2 + 0.25;
    }
    crack(hip, [V(k * 0.08, -0.05, 0.13), V(k * 0.1, -0.22, 0.14), V(k * 0.05, -0.4, 0.15), V(k * 0.09, -0.58, 0.14)], 0.01, shin, 3);
  }

  // Body: a hunched mass of slabs, wider at the shoulders, leaning forward over the maul (one bone, bending at the hips).
  const bodyB = joint(g, V(0, 0.8, -0.02), 'body');
  const body = bodyB.abs;
  slab(body, crust, 0, 0.86, -0.02, 0.27, 1.25, 0.7, 1);
  slab(body, basalt, 0, 1.18, 0.02, 0.36, 1.35, 0.95, 0.95);
  slab(body, basalt, 0, 1.12, -0.18, 0.3, 1.2, 1, 0.9);
  // The open chest: a ring of jagged slabs round the molten heart.
  const core = M(new THREE.IcosahedronGeometry(0.16, 1), heart, body);
  core.position.set(0, 1.2, 0.27);
  const coreGlow = glowSprite(body, '#ff7a1a', 1.35, V(0, 1.2, 0.4), 0.6, true);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + 0.2;
    slab(body, i % 2 ? basalt : ash, Math.cos(a) * 0.25, 1.2 + Math.sin(a) * 0.22, 0.27, 0.075, 1.1, 0.9, 0.8);
  }
  // Seams of fire running out from the heart over the chest and the back.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.5;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 4; j++) {
      const r = 0.2 + j * 0.09;
      pts.push(V(Math.cos(a) * r * 1.2, 1.2 + Math.sin(a) * r * 0.9, 0.3 - j * j * 0.025));
    }
    crack(body, pts, 0.012);
  }
  for (let i = 0; i < 3; i++) crack(body, [V(-0.25 + i * 0.25, 1.45, -0.3), V(-0.2 + i * 0.22, 1.2, -0.42), V(-0.26 + i * 0.25, 0.95, -0.36)]);
  // Shoulders: great boulders crusted with obsidian spikes, smoking.
  for (const k of [-1, 1]) {
    slab(body, crust, k * 0.42, 1.42, -0.02, 0.2, 1.2, 0.85, 1.05);
    for (let i = 0; i < 3; i++) {
      const sp = M(new THREE.ConeGeometry(0.045, 0.24 - i * 0.04, 5), obsidian, body);
      sp.position.set(k * (0.38 + i * 0.07), 1.58 - i * 0.03, -0.06 + i * 0.05);
      sp.rotation.set(-0.3, 0, -k * (0.35 + i * 0.25));
    }
    crack(body, [V(k * 0.3, 1.5, 0.12), V(k * 0.42, 1.44, 0.17), V(k * 0.56, 1.36, 0.08)]);
  }

  // Head: sunk low between the shoulders, a heavy brow, and a crown of obsidian horns.
  const headB = joint(body, V(0, 1.42, 0.14), 'head');
  const head = G(headB.abs, 0, 1.5, 0.2);
  const jawB = joint(head, V(0, -0.07, -0.03), 'jaw');
  const skull = M(new THREE.DodecahedronGeometry(0.13, 0), basalt, head);
  skull.scale.set(1.1, 0.9, 1.05);
  const jaw = M(new THREE.BoxGeometry(0.18, 0.06, 0.14), crust, jawB.abs);
  jaw.position.set(0, -0.1, 0.04);
  M(new THREE.BoxGeometry(0.12, 0.012, 0.02), hot, head).position.set(0, -0.07, 0.11);
  eyesGlow(head, '#ffb347', 0.02, 0.11, 0.05, 0.024);
  for (let i = 0; i < 5; i++) {
    const a = -0.9 + (i / 4) * 1.8;
    const hn = M(new THREE.ConeGeometry(0.03, i === 2 ? 0.3 : 0.22, 5), obsidian, head);
    hn.position.set(Math.sin(a) * 0.12, 0.11, -0.02 + Math.cos(a) * 0.02);
    hn.rotation.set(-0.35, 0, -a * 0.55);
  }

  // Arms: bones at the shoulders and elbows (parts in the shoulder's frame). Left: a fist dripping fire; right: the maul.
  const fores: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const side = k < 0 ? 'L' : 'R';
    const armB = joint(body, V(k * 0.46, 1.36, 0.02), `arm${side}`);
    const sh = armB.p;
    const foreB = joint(sh, V(k * 0.07, -0.4, 0.06), `fore${side}`);
    const fo = foreB.abs;
    bones[`arm${side}`] = armB.p;
    bones[`fore${side}`] = foreB.p;
    slab(sh, basalt, k * 0.05, -0.2, 0.02, 0.12, 1, 1.4, 1);
    M(new THREE.IcosahedronGeometry(0.06, 0), hot, sh).position.set(k * 0.07, -0.4, 0.06);
    slab(fo, crust, k * 0.08, -0.58, 0.06, 0.11, 1, 1.35, 1);
    const fist = slab(fo, basalt, k * 0.09, -0.82, 0.1, 0.14, 1.05, 0.9, 1.1);
    fist.rotation.set(0.2, 0, 0);
    crack(sh, [V(k * 0.04, -0.06, 0.1), V(k * 0.1, -0.28, 0.11), V(k * 0.07, -0.5, 0.13), V(k * 0.11, -0.72, 0.15)], 0.01, fo, 3);
    for (let i = 0; i < 3; i++) {
      const kn = M(new THREE.ConeGeometry(0.025, 0.08, 4), obsidian, fo);
      kn.position.set(k * 0.09 + (i - 1) * 0.06, -0.78, 0.22);
      kn.rotation.x = Math.PI / 2;
    }
    fores.push(fo);
  }
  // Molten drops hanging off the left fist.
  const drips: THREE.Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const d = M(new THREE.SphereGeometry(0.022 - i * 0.004, 6, 5), hot, fores[0]!);
    d.position.set(-0.09 + (i - 1) * 0.05, -0.98 - i * 0.03, 0.1);
    d.scale.y = 1.6;
    drips.push(d);
  }
  // The maul: a haft of black stone, a head of basalt banded with molten iron. Its own bone at the grip.
  const maul = G(fores[1]!, 0.09, -0.84, 0.12);
  maul.name = 'maul';
  maul.rotation.x = 1.2;
  bones.maul = maul;
  M(new THREE.CylinderGeometry(0.035, 0.04, 1.2, 6), ash, maul).position.y = 0.25;
  M(new THREE.CylinderGeometry(0.05, 0.05, 0.12, 6), obsidian, maul).position.y = -0.3;
  const mh = G(maul, 0, 0.88, 0);
  M(new THREE.BoxGeometry(0.46, 0.26, 0.28), basalt, mh);
  for (const k of [-1, 1]) {
    M(new THREE.BoxGeometry(0.06, 0.3, 0.32), crust, mh).position.x = k * 0.25;
    M(new THREE.BoxGeometry(0.03, 0.28, 0.3), hot, mh).position.x = k * 0.19;
  }
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.28, 0.02, 0.012), seam, mh).position.set(0, k * 0.05, 0.145);
  for (const k of [-1, 1]) {
    const sp = M(new THREE.ConeGeometry(0.04, 0.14, 4), obsidian, mh);
    sp.position.set(0, 0.17 * k, 0);
    if (k < 0) sp.rotation.x = Math.PI;
  }
  glowSprite(mh, '#ff6a1f', 0.6, V(0, 0, 0.12), 0.45);

  // Smoke rising off the shoulders, and fire licking out of the crown.
  const wisps: Ribbon[] = [];
  const wm = basic('#2c2420', 0.45);
  for (const k of [-1, 1]) wisps.push(new Ribbon(body, wisp(k * 0.42, -0.02, 0.6, 5).map((p) => p.add(V(0, 1.55, 0))), 0.03, wm, rng, 0.05));
  const crown = flame(head, V(0, 0.12, -0.02), 0.9, rng, '#ff5a1f');
  glowSprite(body, '#ff4f1a', 2.0, V(0, 0.9, 0.2), 0.22);
  const L = pointLight(c, 0xff6a2a, 0.9, 3.5, V(0, 1.2, 0.5));

  // The swing lives in the clips now; the frames only boil (smoke, fire, drips, the heart's pulse).
  const t = (n: number): void => {
    for (const r of wisps) r.step();
    crown.flick();
    drips.forEach((d, i) => { d.position.y = -0.98 - i * 0.03 - ((n + i) % 3) * 0.025; });
    const pulse = 0.45 + ((n * 3) % 10) / 22;
    coreGlow.material.opacity = pulse;
    L.intensity = (0.6 + pulse) * LIGHT_K;
  };
  t(0);
  return done(c, 2.05, t, undefined, bonesOf(c, {
    body: bodyB.p, head: headB.p, jaw: jawB.p, armL: bones.armL!, foreL: bones.foreL!, armR: bones.armR!, foreR: bones.foreR!, maul,
    legL: bones.legL!, shinL: bones.shinL!, legR: bones.legR!, shinR: bones.shinR!,
  }));
};

/**
 * El Coloso de Obsidiana: the boss of the Obsidiana incursions and the deep crypt's last hall. A giant of
 * black volcanic glass cut in sharp facets, split by thin violet seams; a red-violet heart burns in its open
 * chest, crystals jut from its shoulders and back like a geode turned inside out, and its fists are clubs of
 * fused glass.
 *
 * `shining`: the shine (it turns blows back): the glass goes to a mirror of white-violet light, a faceted
 * shell of light closes over it and the heart blazes, so at a glance it reads «don't hit it now».
 */
export const obsidianColossus = (o?: BaseOpts & { shining?: boolean }): Model => {
  const c = begin(o, 1.0, 47);
  const { g, rng } = c;
  const lit = o?.shining === true;
  // Glass: almost black with a cold sheen; the shine turns it to a lit mirror.
  const glass = lit
    ? emis('#f4f0ff', '#b9a6ff', 1.25, { roughness: 0.05, metalness: 0.5, flatShading: true, bumpScale: 0 })
    : std('#1a1724', { roughness: 0.16, metalness: 0.35, flatShading: true, bumpScale: 0 });
  const glassB = lit
    ? emis('#ffffff', '#dcd2ff', 1.5, { roughness: 0.05, metalness: 0.5, flatShading: true, bumpScale: 0 })
    : std('#2a2536', { roughness: 0.2, metalness: 0.3, flatShading: true, bumpScale: 0 });
  const seamM = lit ? emis('#ffffff', '#ffffff', 2.6) : emis('#9a7ad8', '#7a4dff', 0.9);
  const heart = lit ? emis('#ffffff', '#ff8ab0', 3.2) : emis('#ffd6e6', '#ff2f62', 2.6);
  const tipM = lit ? emis('#ffffff', '#efe8ff', 2.2) : emis('#c9b8ff', '#6a3dff', 0.9);
  /** A crystal: a six-sided prism with a pointed cap, standing on its base (then turned by the caller). */
  const crystal = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, r: number, h: number, rx: number, rz: number, tip = false): THREE.Group => {
    const k = G(parent, x, y, z);
    k.rotation.set(rx, rng() * Math.PI, rz);
    M(new THREE.CylinderGeometry(r * 0.82, r, h, 6), mat, k).position.y = h / 2;
    M(new THREE.ConeGeometry(r * 0.82, h * 0.5, 6), tip ? tipM : mat, k).position.y = h + h * 0.25;
    return k;
  };
  /** A faceted lump of glass, a little crooked. */
  const lump = (parent: THREE.Object3D, mat: THREE.Material, geo: THREE.BufferGeometry, x: number, y: number, z: number, sx: number, sy: number, sz: number): THREE.Mesh => {
    const m = M(geo, mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    m.rotation.set((rng() - 0.5) * 0.6, rng() * 3, (rng() - 0.5) * 0.6);
    return m;
  };
  /** A thin seam of violet light along the glass: short straight runs, like a fracture. */
  const seam = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.006): void => {
    for (let i = 0; i + 1 < pts.length; i++) limb(parent, pts[i] as THREE.Vector3, pts[i + 1] as THREE.Vector3, r, r, seamM, 3);
  };
  const ico = new THREE.IcosahedronGeometry(1, 0);
  const oct = new THREE.OctahedronGeometry(1, 0);
  const dod = new THREE.DodecahedronGeometry(1, 0);

  // Legs: two great prisms of glass, crystal knees, wedge feet. Bones at the hips (parts in the hip's frame) and knees.
  const bones: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const side = k < 0 ? 'L' : 'R';
    const hipB = joint(g, V(k * 0.3, 0.8, 0), `leg${side}`);
    const hip = hipB.p;
    const kneeB = joint(hip, V(0, -0.38, 0.03), `shin${side}`);
    const low = kneeB.abs;
    bones[`leg${side}`] = hipB.p;
    bones[`shin${side}`] = kneeB.p;
    const thigh = M(new THREE.CylinderGeometry(0.15, 0.12, 0.42, 6), glass, hip);
    thigh.position.y = -0.2;
    const shin = M(new THREE.CylinderGeometry(0.13, 0.17, 0.42, 6), glassB, low);
    shin.position.set(0, -0.56, 0.02);
    crystal(hip, glassB, k * 0.04, -0.4, 0.12, 0.05, 0.14, 0.9, k * 0.3, true);
    lump(low, glass, oct, 0, -0.77, 0.07, 0.2, 0.08, 0.27);
    seam(hip, [V(k * 0.1, -0.02, 0.1), V(k * 0.12, -0.2, 0.11), V(k * 0.08, -0.38, 0.13)]);
    seam(low, [V(k * 0.08, -0.38, 0.13), V(k * 0.13, -0.6, 0.15)]);
  }

  // The body: a hip block, a great faceted chest leaning forward, a heart in the open breast (one bone, bending at the hips).
  const bodyB = joint(g, V(0, 0.9, 0), 'body');
  const body = bodyB.abs;
  // Secondary bones: the crystal clusters on the shoulders and the spines down the back shiver and lag.
  const crystB = [-1, 1].map((k) => joint(body, V(k * 0.49, 1.63, -0.02), k < 0 ? 'crystL' : 'crystR'));
  const spinesB = joint(body, V(0, 1.45, -0.3), 'spines');
  lump(body, glassB, oct, 0, 0.9, 0, 0.36, 0.18, 0.26);
  const chest = lump(body, glass, ico, 0, 1.36, -0.02, 0.52, 0.46, 0.4);
  chest.rotation.set(0.12, 0.3, 0);
  lump(body, glassB, ico, 0, 1.14, -0.1, 0.4, 0.3, 0.32);
  const core = M(new THREE.OctahedronGeometry(0.15, 0), heart, body);
  core.position.set(0, 1.36, 0.33);
  core.scale.set(1, 1.35, 0.8);
  const coreGlow = glowSprite(body, lit ? '#ffd0e8' : '#ff3d5a', lit ? 2.0 : 1.4, V(0, 1.36, 0.45), lit ? 0.9 : 0.6, true);
  // Splinters round the heart, pointing out, as if the glass burst open from inside.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const sp = M(new THREE.ConeGeometry(0.035, 0.2, 4), glassB, body);
    sp.position.set(Math.cos(a) * 0.21, 1.36 + Math.sin(a) * 0.24, 0.3);
    sp.rotation.set(Math.PI / 2 - 0.2, 0, -a + Math.PI / 2);
  }
  // Fractures running out of the heart over the chest.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 4; j++) {
      const r = 0.2 + j * 0.1;
      pts.push(V(Math.cos(a) * r * 1.1 + (rng() - 0.5) * 0.04, 1.36 + Math.sin(a) * r * 0.85, 0.36 - j * j * 0.03));
    }
    seam(body, pts, 0.007);
  }
  // Shoulders: clusters of crystal jutting up and out, a geode turned inside out.
  for (const k of [-1, 1]) {
    lump(body, glassB, dod, k * 0.48, 1.6, -0.02, 0.2, 0.16, 0.2);
    for (let i = 0; i < 5; i++) {
      const t = i / 4;
      crystal(crystB[k < 0 ? 0 : 1]!.abs, i % 2 ? glassB : glass, k * (0.42 + t * 0.14), 1.66 - t * 0.06, -0.12 + t * 0.2, 0.05 + (i === 2 ? 0.02 : 0) - t * 0.01, 0.26 + rng() * 0.18 - t * 0.08, -0.35 + t * 0.5, -k * (0.25 + t * 0.6), i === 2);
    }
  }
  // Spines of glass down its back.
  for (let i = 0; i < 4; i++) crystal(spinesB.abs, i % 2 ? glass : glassB, (rng() - 0.5) * 0.24, 1.6 - i * 0.16, -0.32 + i * 0.02, 0.06, 0.3 - i * 0.04, -0.9 - i * 0.12, (rng() - 0.5) * 0.4, i === 0);

  // Head: a small sharp shard sunk between the shoulders, a slit of red-violet light for eyes.
  const headB = joint(body, V(0, 1.7, 0.12), 'head');
  const head = G(headB.abs, 0, 1.78, 0.16);
  const skull = M(oct, glass, head);
  skull.scale.set(0.13, 0.19, 0.13);
  skull.rotation.y = Math.PI / 4;
  M(new THREE.BoxGeometry(0.15, 0.022, 0.02), heart, head).position.set(0, 0.0, 0.1);
  glowSprite(head, lit ? '#ffffff' : '#ff4f7a', 0.34, V(0, 0, 0.13), 0.85);
  for (let i = 0; i < 3; i++) crystal(head, glassB, (i - 1) * 0.07, 0.1, -0.02, 0.022, 0.16 + (i === 1 ? 0.08 : 0), -0.2, (1 - i) * 0.35, i === 1);

  // Arms: bones at the shoulders and elbows (parts in the shoulder's frame); each ends in a club of fused glass.
  for (const k of [-1, 1]) {
    const side = k < 0 ? 'L' : 'R';
    const armB = joint(body, V(k * 0.56, 1.5, 0.02), `arm${side}`);
    const sh = armB.p;
    const foreB = joint(sh, V(k * 0.06, -0.39, 0.03), `fore${side}`);
    const fo = foreB.abs;
    bones[`arm${side}`] = armB.p;
    bones[`fore${side}`] = foreB.p;
    const up = M(new THREE.CylinderGeometry(0.11, 0.09, 0.4, 6), glassB, sh);
    up.position.set(k * 0.04, -0.2, 0);
    const fore = M(new THREE.CylinderGeometry(0.09, 0.14, 0.42, 6), glass, fo);
    fore.position.set(k * 0.07, -0.58, 0.04);
    lump(fo, glassB, dod, k * 0.08, -0.88, 0.07, 0.19, 0.17, 0.19);
    for (let i = 0; i < 4; i++) {
      const sp = M(new THREE.ConeGeometry(0.035, 0.16, 4), glass, fo);
      const a = (i / 4) * Math.PI * 2;
      sp.position.set(k * 0.08 + Math.cos(a) * 0.16, -0.9 + Math.sin(a) * 0.05, 0.07 + Math.sin(a) * 0.14);
      sp.rotation.set(Math.sin(a) * 1.2, 0, -Math.cos(a) * 1.2);
    }
    seam(sh, [V(k * 0.02, -0.04, 0.1), V(k * 0.07, -0.26, 0.1)]);
    seam(fo, [V(k * 0.07, -0.26, 0.1), V(k * 0.05, -0.46, 0.13), V(k * 0.1, -0.72, 0.16)]);
  }

  // Splinters hanging in the air round it, turning (they glint with the shine).
  const motes = G(g, 0, 1.4, 0);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const m = M(new THREE.OctahedronGeometry(0.05, 0), i % 2 ? tipM : glassB, motes);
    m.position.set(Math.cos(a) * 0.82, (rng() - 0.5) * 0.6, Math.sin(a) * 0.62);
    m.scale.set(0.6, 1.8, 0.6);
  }

  if (lit) {
    // The mirror shell: a faceted husk of light over the whole body.
    const shellM = cached('colossus-shell', () => new THREE.MeshBasicMaterial({ color: '#e6dcff', transparent: true, opacity: 0.2, depthWrite: false, blending: THREE.AdditiveBlending }));
    const husk = M(new THREE.IcosahedronGeometry(1, 1), shellM, body);
    husk.position.y = 1.15;
    husk.scale.set(0.95, 1.15, 0.8);
    const wire = cached('colossus-shell-wire', () => new THREE.MeshBasicMaterial({ color: '#ffffff', wireframe: true, transparent: true, opacity: 0.35, depthWrite: false, blending: THREE.AdditiveBlending }));
    const net = M(new THREE.IcosahedronGeometry(1, 1), wire, body);
    net.position.y = 1.15;
    net.scale.set(0.97, 1.17, 0.82);
    glowSprite(g, '#e6dcff', 3.6, V(0, 1.2, 0), 0.4);
  }
  glowSprite(g, lit ? '#d9ccff' : '#6a3dff', lit ? 2.6 : 1.8, V(0, 0.9, 0.2), lit ? 0.35 : 0.08);
  const L = pointLight(c, lit ? 0xd9ccff : 0xff3d74, lit ? 1.4 : 0.8, 3.5, V(0, 1.3, 0.6));

  // The blows live in the clips now; the frames only boil (the splinters turning round it, the heart's pulse).
  const t = (n: number): void => {
    motes.rotation.y = n * 0.45;
    motes.position.y = 1.4 + (n % 2) * 0.05;
    const pulse = 0.5 + ((n * 3) % 10) / 20;
    coreGlow.material.opacity = (lit ? 0.75 : 0.45) + pulse * 0.3;
    L.intensity = (0.6 + pulse) * LIGHT_K * (lit ? 1.4 : 1);
  };
  t(0);
  return done(c, 2.15, t, undefined, bonesOf(c, {
    body: bodyB.p, head: headB.p, crystL: crystB[0]!.p, crystR: crystB[1]!.p, spines: spinesB.p,
    armL: bones.armL!, foreL: bones.foreL!, armR: bones.armR!, foreR: bones.foreR!, legL: bones.legL!, shinL: bones.shinL!, legR: bones.legR!, shinR: bones.shinR!,
  }));
};
